import { useModalState } from "@clipboard-health/ui-react";
import { Button } from "@mui/material";
import {
  MissingDocumentsAlert,
  OverworkBookingAlert,
  PendingDocumentsAlert,
  WaitingStripeResponseAlert,
} from "@src/app/dayView/Alerts";
import { useMissingDocuments } from "@src/app/dayView/custom-hooks/useMissingDocuments";
import { ShiftItemCategory } from "@src/app/dayView/model";
import { AppV2AccountRoutes } from "@src/app/routing/constant/appV2AccountRoutes";
import { ActionType } from "@src/app/store/session";
import { useAgentProfile } from "@src/appV2/Agents/api/useAgentProfile";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BookabilityAttributes,
  BookabilityDecision,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { PaymentServiceAccountStatus, type Shift } from "@src/lib/interface/src";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

export interface BlockedShiftButtonProps {
  shiftBookability: BookabilityAttributes & { decision: BookabilityDecision.BLOCKED };
  shift: Shift;
}

export function BlockedShiftButton(props: BlockedShiftButtonProps) {
  const { shiftBookability, shift } = props;

  const { data: agent, isSuccess: isAgentSuccess } = useAgentProfile();

  const { showErrorToast } = useToast();
  const storeDispatch = useDispatch();

  const {
    missingDocuments,
    pendingDocuments,
    loading: isLoadingMissingOrPendingDocuments,
  } = useMissingDocuments(shift);

  const history = useHistory();

  const {
    modalIsOpen: waitingStripeResponseModalIsOpen,
    openModal: openWaitingStripeResponseModal,
    closeModal: closeWaitingStripeResponseModal,
  } = useModalState();

  const {
    modalIsOpen: overworkBookingAlertIsOpen,
    openModal: openOverworkBookingAlert,
    closeModal: closeOverworkBookingAlert,
  } = useModalState();

  const {
    modalIsOpen: missingDocumentsAlertIsOpen,
    openModal: openMissingDocumentsAlert,
    closeModal: closeMissingDocumentsAlert,
  } = useModalState();

  const {
    modalIsOpen: pendingDocumentsAlertIsOpen,
    openModal: openPendingDocumentsAlert,
    closeModal: closePendingDocumentsAlert,
  } = useModalState();

  return (
    <>
      {shiftBookability.unmetCriteria[0] ===
      BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS ? (
        <Button
          variant="outlined"
          size="small"
          disabled={isLoadingMissingOrPendingDocuments}
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS,
              shiftId: shift._id,
            });

            if (missingDocuments.size > 0) {
              openMissingDocumentsAlert();
              return;
            }

            if (pendingDocuments.size > 0) {
              openPendingDocumentsAlert();
              return;
            }

            showErrorToast(
              "Something went wrong while checking your documents. Please reload the application and try again."
            );
          }}
        >
          <b>Upload Required Documents</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria[0] === BookabilityUnmetCriteria.WORKER_PENDING_DOCUMENTS ? (
        <Button
          variant="outlined"
          size="small"
          disabled={isLoadingMissingOrPendingDocuments}
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: BookabilityUnmetCriteria.WORKER_PENDING_DOCUMENTS,
            });
            openPendingDocumentsAlert();
          }}
        >
          <b>View Pending Documents</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria[0] === BookabilityUnmetCriteria.SHIFT_OVERWORKING ? (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: BookabilityUnmetCriteria.SHIFT_OVERWORKING,
            });
            openOverworkBookingAlert();
          }}
        >
          <b>Unable to book shift</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria[0] === BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED ? (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED,
            });

            const isAgentPendingInPaymentService =
              isAgentSuccess && agent.canBeOnboardedInStripe
                ? [
                    PaymentServiceAccountStatus.ACCOUNT_CREATED,
                    PaymentServiceAccountStatus.ACCOUNT_NEEDS_UPDATE,
                    PaymentServiceAccountStatus.INSTANT_PAYOUT_PENDING,
                    PaymentServiceAccountStatus.STANDARD_PAYOUT_PENDING,
                  ].includes(agent?.paymentAccountInfo?.status as PaymentServiceAccountStatus)
                : false;

            if (isAgentPendingInPaymentService) {
              history.push("/home/onboardingPayments");
            } else {
              openWaitingStripeResponseModal();
            }
          }}
        >
          <b>Enable Payments</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria[0] === BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT ? (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: shiftBookability.unmetCriteria,
            });

            storeDispatch({
              type: ActionType.SHOW_IC_AGREEMENT_POPUP,
            });
          }}
        >
          <b>Sign Agreement</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria[0] ===
      BookabilityUnmetCriteria.WORKER_DISABLED_TEST_ACCOUNT ? (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_BUTTON_FOR_BLOCKED_BOOKABILITY_STATUS, {
              unmetCriteria: shiftBookability.unmetCriteria,
            });
            showErrorToast("You cannot book this shift with a test account");
          }}
        >
          <b>Disabled (Test Account)</b>
        </Button>
      ) : null}

      {shiftBookability.unmetCriteria.length === 0 ||
      ![
        BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS,
        BookabilityUnmetCriteria.WORKER_PENDING_DOCUMENTS,
        BookabilityUnmetCriteria.SHIFT_OVERWORKING,
        BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED,
        BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT,
        BookabilityUnmetCriteria.WORKER_DISABLED_TEST_ACCOUNT,
      ].includes(shiftBookability.unmetCriteria[0]) ? (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.TAPPED_SHIFT_UNAVAILABLE_FOR_BLOCKED_BOOKABILITY_STATUS, {
              bookabilityDecision: shiftBookability.decision,
              unmetCriteria: shiftBookability.unmetCriteria,
              shiftId: shift._id,
            });
            showErrorToast("Sorry, this shift is no longer available");
          }}
        >
          <b>Shift Unavailable</b>
        </Button>
      ) : null}

      <MissingDocumentsAlert
        isOpen={missingDocumentsAlertIsOpen}
        category={ShiftItemCategory.MISSING_CBH_DOCUMENTS}
        missingDocuments={missingDocuments}
        expiringInFuture={false}
        onConfirm={() => {
          history.push(AppV2AccountRoutes.DOCUMENTS);
        }}
        onDidDismiss={closeMissingDocumentsAlert}
      />

      <PendingDocumentsAlert
        isOpen={pendingDocumentsAlertIsOpen}
        shift={shift}
        category={ShiftItemCategory.WAITING_FOR_APPROVE}
        onConfirm={() => {
          history.push(AppV2AccountRoutes.DOCUMENTS);
        }}
        onDidDismiss={closePendingDocumentsAlert}
      />

      <WaitingStripeResponseAlert
        isOpen={waitingStripeResponseModalIsOpen}
        onDidDismiss={closeWaitingStripeResponseModal}
      />

      <OverworkBookingAlert
        isOpen={overworkBookingAlertIsOpen}
        onDidDismiss={closeOverworkBookingAlert}
      />
    </>
  );
}
